import { call, put, takeLatest, select } from 'redux-saga/effects';
import { headerActions } from './slice';
import { notificationAlertActions } from 'app/pages/NotificationAlert/slice';
import { selectDealersList, selectDealerSelected } from 'app/pages/Header/selectors';
import queryClient from 'app/apiService/queryClient';
import {
  DEALERS_QUERY_KEY,
  fetchDealersListForHeader,
  findDealerInDealersList,
} from './utils/refreshDealersList';

export function* getDealers({ payload }: ReturnType<any>) {
  try {
    yield put(headerActions.setLoading(true));

    const forceRefresh = Boolean(payload?.forceRefresh);

    const cachedData = queryClient.getQueryData(DEALERS_QUERY_KEY);
    if (cachedData && !forceRefresh) {
      yield put(headerActions.requestDealersSuccess(cachedData));
      return;
    }

    const responseData = yield call(fetchDealersListForHeader);

    if (responseData.body?.items?.length) {
      yield put(headerActions.requestDealersSuccess(responseData));

      const selectDealerId = payload?.selectDealerId;

      if (selectDealerId) {
        const dealerToSelect = findDealerInDealersList(
          responseData.body.items,
          selectDealerId,
        );

        if (dealerToSelect) {
          localStorage.setItem('dealer', JSON.stringify(dealerToSelect));
          yield put(headerActions.selectDealer(dealerToSelect));
        }
      } else {
        const selectedDealerState = yield select(selectDealerSelected);
        const selectedDealerId =
          selectedDealerState && typeof selectedDealerState === 'object'
            ? selectedDealerState.dealer || selectedDealerState._id
            : selectedDealerState;

        if (selectedDealerId && typeof selectedDealerId === 'string') {
          const updatedSelectedDealer = findDealerInDealersList(
            responseData.body.items,
            selectedDealerId,
          );

          if (updatedSelectedDealer) {
            localStorage.setItem(
              'dealer',
              JSON.stringify(updatedSelectedDealer),
            );
            yield put(headerActions.selectDealer(updatedSelectedDealer));
          }
        }
      }
    } else {
      yield put(headerActions.requestDealersError(new Error('No dealers found')));
      yield put(
        notificationAlertActions.addNotification({
          open: true,
          severity: 'warning',
          message: 'No dealers found',
        }),
      );
    }
  } catch (err: any) {
    console.log(err);
    let errorMessage = 'Error fetching dealers list';

    if (err.response?.status === 404) {
      errorMessage = 'Dealers API endpoint not found';
    } else if (err.message === 'Server Error') {
      errorMessage = 'Server error occurred while fetching dealers';
    }

    yield put(headerActions.requestDealersError(err));
    yield put(
      notificationAlertActions.addNotification({
        open: true,
        severity: 'error',
        message: errorMessage,
      }),
    );
  } finally {
    yield put(headerActions.setLoading(false));
  }
}

export function* setDealer({ payload }: ReturnType<any>) {
  try {
    const dealersState = yield select(selectDealersList);
    const dealers = dealersState.data?.body?.items || [];

    let dealer = dealers?.find(item => item._id === payload.dealerId);

    if (dealer) {
      localStorage.setItem('dealer', JSON.stringify(dealer));
      yield put(headerActions.selectDealer(dealer));
    } else {
      if (payload.history) {
        payload.history.goBack();
        throw new Error('dealer not found');
      }
    }
  } catch (error) {
    console.log(error);
    yield put(
      notificationAlertActions.addNotification({
        open: true,
        severity: 'error',
        message: 'Could not select the dealer',
      }),
    );
  }
}

export function* headerSaga() {
  yield takeLatest(headerActions.requestDealers.type, getDealers);
  yield takeLatest(headerActions.setDealer.type, setDealer);
}
